import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import {
  Plus,
  Sparkles,
  FileText,
  Mic,
  Lock,
  History,
  ClipboardList,
  GripVertical,
  Search,
  Trash2,
  ExternalLink,
} from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { API_BASE } from "@/config/api"
import { formatDateTime } from "@/lib/format"
import { toast } from "sonner"
import { SignaturePad } from "@/components/app/SignaturePad"
import { saveDraft, loadDraft, clearDraft, syncPendingDrafts } from "@/lib/note-draft-store"
import { useClinicalContext } from "@/hooks/use-clinical-context"
import { CLINICAL_VERTICALS, labelForVertical } from "@/config/clinical-verticals"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

const API = API_BASE
const SECTIONS = ["subjective", "objective", "assessment", "plan"]
const NOTE_STATUSES = ["draft", "signed"]
const NOTE_CATEGORY_KEYS = [
  "psych_eval",
  "therapy",
  "scales",
  "child",
  "consent",
  "report",
  "physio",
  "kineto",
  "chiro",
  "speech",
  "nutrition",
  "manual",
  "home_care",
]

function sortNoteTemplates(list) {
  return [...list].sort(
    (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.name || "").localeCompare(b.name || "")
  )
}

function statusBadgeVariant(status) {
  return status === "signed" ? "default" : "secondary"
}

function SortableSoapShell({ id, label, isSigned, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative pl-8 border-l-2 border-border hover:border-accent transition-colors"
    >
      {!isSigned && (
        <button
          type="button"
          className="absolute left-0 top-0 p-1 rounded text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
          aria-label="Reorder section"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{label}</p>
      {children}
    </div>
  )
}

export default function Notes() {
  const { t } = useTranslation()
  const [notes, setNotes] = useState([])
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(true)
  const [clients, setClients] = useState([])
  const [selected, setSelected] = useState(null)
  const [draft, setDraft] = useState(null)
  const [savedAt, setSavedAt] = useState(null)
  const [templates, setTemplates] = useState([])
  const [filterCategory, setFilterCategory] = useState("__all__")
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState("")
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [editorTemplateId, setEditorTemplateId] = useState("")
  const [newOpen, setNewOpen] = useState(false)
  const [newClientId, setNewClientId] = useState("")
  const [newTplName, setNewTplName] = useState("")
  const [listening, setListening] = useState(false)
  const [versions, setVersions] = useState([])
  const [aiConsentOpen, setAiConsentOpen] = useState(false)
  const [pendingAi, setPendingAi] = useState(null)
  const [supervision, setSupervision] = useState(null)
  const [signOpen, setSignOpen] = useState(false)
  const [signatureData, setSignatureData] = useState(null)
  const [icdQuery, setIcdQuery] = useState("")
  const [icdResults, setIcdResults] = useState([])
  const [phqScore, setPhqScore] = useState("")
  const [sectionOrder, setSectionOrder] = useState(() => [...SECTIONS])
  const [draftLoading, setDraftLoading] = useState(false)
  const [filterStatus, setFilterStatus] = useState("")
  const [filterClient, setFilterClient] = useState("")
  const [searchQ, setSearchQ] = useState("")
  const [params, setParams] = useSearchParams()
  const saveTimer = useRef(null)
  const selectSeq = useRef(0)
  const initialSelectDone = useRef(false)
  const queryHandled = useRef("")
  const [bootstrapAppointmentId, setBootstrapAppointmentId] = useState("")
  const noteIdParam = params.get("note") || ""
  const filterParam = params.get("filter") || ""
  const { hasPsychTools, effectiveVertical, enabledVerticals, setWorkingVertical } = useClinicalContext()
  const workingVertical = effectiveVertical || "psych"
  const showPsychNoteTools = workingVertical === "psych" && hasPsychTools

  const filterClientName = useMemo(
    () => clients.find((c) => c.id === filterClient)?.name || "",
    [clients, filterClient]
  )

  const verticalOptions = useMemo(() => {
    const enabled = new Set(enabledVerticals || ["psych"])
    return CLINICAL_VERTICALS.filter((v) => enabled.has(v.value))
  }, [enabledVerticals])

  const categoryOptions = useMemo(() => {
    const vert = workingVertical
    const cats = new Set(
      templates.map((tpl) => tpl.category).filter((c) => c && c !== "general" && c !== "custom")
    )
    if (!cats.size) {
      if (vert === "psych") NOTE_CATEGORY_KEYS.filter((c) => ["psych_eval", "therapy", "scales", "child", "consent", "report"].includes(c)).forEach((c) => cats.add(c))
      else if (vert === "physio") cats.add("physio")
      else if (vert === "kineto") cats.add("kineto")
      else if (vert === "speech") cats.add("speech")
      else if (vert === "nutrition") cats.add("nutrition")
      else if (vert === "osteo") cats.add("manual")
      else if (vert === "home_care") cats.add("home_care")
    }
    return [...cats].sort()
  }, [templates, workingVertical])

  const soapLabels = useMemo(
    () => ({
      subjective: t("app.notes.soap.subjective"),
      objective: t("app.notes.soap.objective"),
      assessment: t("app.notes.soap.assessment"),
      plan: t("app.notes.soap.plan"),
    }),
    [t]
  )

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const hasAiConsent = () => localStorage.getItem("asistentpersonal-ai-consent") === "1"
  const ensureAiConsent = (action) => {
    if (hasAiConsent()) return action()
    setPendingAi(() => action)
    setAiConsentOpen(true)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const q = {}
      if (filterStatus) q.status = filterStatus
      if (filterClient) q.client_id = filterClient
      const { data: notesData } = await axios.get(`${API}/notes`, { params: q })
      if (Array.isArray(notesData)) {
        setNotes(notesData)
        setSummary({
          total: notesData.length,
          draft: notesData.filter((n) => n.status === "draft").length,
          signed: notesData.filter((n) => n.status === "signed").length,
        })
      } else {
        setNotes(notesData.notes || [])
        setSummary(notesData.summary || {})
      }
    } catch {
      toast.error(t("app.notes.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [filterStatus, filterClient, t])

  const loadTemplatesForVertical = useCallback(async (vertical, category) => {
    const params = {}
    if (vertical) params.vertical = vertical
    if (category && category !== "__all__") params.category = category
    const { data } = await axios.get(`${API}/note-templates`, { params })
    return sortNoteTemplates(data || [])
  }, [])

  const refreshTemplates = useCallback(
    async (vertical, category, keepTemplateId) => {
      const vert = vertical || effectiveVertical || "psych"
      setTemplatesLoading(true)
      try {
        const list = await loadTemplatesForVertical(vert, category)
        setTemplates(list)
        const tpl =
          list.find((x) => x.id === keepTemplateId) ||
          list.find((x) => x.name === keepTemplateId) ||
          list[0] ||
          null
        setSelectedTemplateId(tpl?.id || tpl?.name || "")
        setPreviewTemplate(tpl)
        if (tpl?.name) setNewTplName((prev) => prev || tpl.name)
        return list
      } catch {
        setTemplates([])
        setPreviewTemplate(null)
        setSelectedTemplateId("")
        return []
      } finally {
        setTemplatesLoading(false)
      }
    },
    [effectiveVertical, loadTemplatesForVertical]
  )

  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(r.data || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!workingVertical) return
    refreshTemplates(workingVertical, filterCategory, "")
  }, [workingVertical, filterCategory, refreshTemplates])

  useEffect(() => {
    if (!selected || !templates.length) return
    const inList = templates.some((t) => t.id === editorTemplateId || t.name === editorTemplateId)
    if (editorTemplateId && !inList) setEditorTemplateId("")
  }, [workingVertical, templates, selected?.id, editorTemplateId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (filterParam === "unsigned") setFilterStatus("draft")
  }, [filterParam])

  useEffect(() => {
    const appointmentId = params.get("appointment") || ""
    const clientId = params.get("client") || ""
    const action = params.get("action") || ""
    const key = `${appointmentId}|${clientId}|${action}`
    if (key === "||") return
    if (queryHandled.current === key) return
    queryHandled.current = key

    if (appointmentId) {
      axios
        .get(`${API}/notes/bootstrap`, { params: { appointment_id: appointmentId } })
        .then(({ data }) => {
          setBootstrapAppointmentId(data.appointment_id || appointmentId)
          if (data.client_id) {
            setNewClientId(data.client_id)
            setFilterClient(data.client_id)
          }
          const tpl = data.template
          if (tpl?.name) setNewTplName(tpl.name)
          setNewOpen(true)
        })
        .catch(() => {})
      return
    }

    if (clientId) {
      setFilterClient(clientId)
      setNewClientId(clientId)
      if (action === "new") setNewOpen(true)
      return
    }

    if (action === "new") setNewOpen(true)
  }, [params])

  useEffect(() => {
    if (!notes.length) return
    if (noteIdParam) {
      const pick = notes.find((n) => n.id === noteIdParam)
      if (pick && pick.id !== selected?.id) selectNote(pick)
      return
    }
    if (filterClient) {
      const forClient = notes.filter((n) => n.client_id === filterClient)
      if (forClient.length && (!selected || selected.client_id !== filterClient)) {
        selectNote(forClient[0])
        initialSelectDone.current = true
        return
      }
      if (!forClient.length) {
        setSelected(null)
        setDraft(null)
      }
    }
    if (!initialSelectDone.current && notes.length) {
      initialSelectDone.current = true
      selectNote(notes[0])
    }
  }, [notes, noteIdParam, filterClient])

  useEffect(() => {
    syncPendingDrafts((noteId, d) => axios.patch(`${API}/notes/${noteId}`, d)).catch(() => {})
  }, [])

  useEffect(() => {
    setSectionOrder([...SECTIONS])
  }, [selected?.id])

  const clearClientFilter = () => {
    setFilterClient("")
    const next = new URLSearchParams(params)
    next.delete("client")
    next.delete("action")
    setParams(next, { replace: true })
    queryHandled.current = ""
  }

  const openComposeForFiltered = () => {
    if (filterClient) setNewClientId(filterClient)
    setNewOpen(true)
  }

  const visibleNotes = useMemo(() => {
    const q = searchQ.trim().toLowerCase()
    if (!q) return notes
    return notes.filter(
      (n) =>
        (n.client_name || "").toLowerCase().includes(q) ||
        (n.subjective || "").toLowerCase().includes(q) ||
        (n.assessment || "").toLowerCase().includes(q)
    )
  }, [notes, searchQ])

  async function selectNote(n) {
    if (!n?.id) return
    if (saveTimer.current) {
      clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    const seq = ++selectSeq.current
    setSelected(n)
    setDraft(null)
    setDraftLoading(true)
    setIcdQuery("")
    setIcdResults([])
    setPhqScore("")
    setVersions([])
    try {
      const cached = await loadDraft(n.id)
      if (seq !== selectSeq.current) return
      const base = cached || {
        subjective: n.subjective || "",
        objective: n.objective || "",
        assessment: n.assessment || "",
        plan: n.plan || "",
      }
      setDraft(base)
      const tplHit = templates.find(
        (tpl) => (tpl.subjective || "").trim() && (tpl.subjective || "").trim() === (base.subjective || "").trim()
      )
      setEditorTemplateId(tplHit?.id || tplHit?.name || "")
      axios
        .get(`${API}/notes/${n.id}/versions/full`)
        .then((r) => {
          if (seq === selectSeq.current) setVersions(r.data || [])
        })
        .catch(() => {
          if (seq === selectSeq.current) setVersions([])
        })
    } finally {
      if (seq === selectSeq.current) setDraftLoading(false)
    }
  }

  const saveNote = useCallback(
    async (noteId, data) => {
      if (!noteId || selected?.status === "signed") return
      try {
        const { data: updated } = await axios.patch(`${API}/notes/${noteId}`, data)
        setSelected(updated)
        setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)))
        setSavedAt(new Date())
      } catch {
        toast.error(t("app.notes.toast.saveFailed"))
      }
    },
    [selected?.status, t]
  )

  useEffect(() => {
    if (draftLoading || !selected?.id || !draft || selected.status === "signed") return
    const noteId = selected.id
    const timer = setTimeout(() => {
      if (selected?.id === noteId) saveDraft(noteId, draft)
    }, 600)
    return () => clearTimeout(timer)
  }, [draft, draftLoading, selected?.id, selected?.status])

  const onFieldChange = (key, value) => {
    const next = { ...draft, [key]: value }
    setDraft(next)
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      if (selected?.id) saveNote(selected.id, { [key]: value })
    }, 800)
  }

  const signNote = async () => {
    if (!selected?.id || !signatureData) return
    try {
      const { data } = await axios.post(`${API}/notes/${selected.id}/sign`, {
        signature_data: signatureData,
      })
      setSelected(data)
      setNotes((prev) => prev.map((n) => (n.id === data.id ? data : n)))
      await clearDraft(selected.id)
      setSignOpen(false)
      setSignatureData(null)
      toast.success(t("app.notes.toast.signed"))
      load()
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.notes.toast.signFailed"))
    }
  }

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API}/notes/${id}`)
      if (selected?.id === id) {
        setSelected(null)
        setDraft(null)
      }
      toast.success(t("app.notes.toast.deleted"))
      load()
    } catch (e) {
      toast.error(e.response?.data?.detail || t("app.notes.toast.deleteFailed"))
    }
  }

  const searchIcd = async (q) => {
    setIcdQuery(q)
    if (q.length < 2) return setIcdResults([])
    try {
      const { data } = await axios.get(`${API}/ai/icd10`, { params: { q } })
      setIcdResults(data.results || [])
    } catch {
      setIcdResults([])
    }
  }

  const appendIcd = (code, label) => {
    const line = `${code} — ${label}`
    onFieldChange("assessment", draft.assessment ? `${draft.assessment}\n${line}` : line)
    setIcdResults([])
    setIcdQuery("")
  }

  const submitPhq = async () => {
    if (!selected?.client_id || phqScore === "") return
    try {
      await axios.post(`${API}/clients/${selected.client_id}/assessments`, {
        scale_type: "PHQ-9",
        score: parseInt(phqScore, 10),
      })
      setPhqScore("")
      toast.success(t("app.notes.toast.phq9Saved"))
    } catch {
      toast.error(t("app.notes.toast.phq9Failed"))
    }
  }

  const runSupervision = () =>
    ensureAiConsent(async () => {
      try {
        const { data } = await axios.post(`${API}/ai/supervision/weekly-summary`)
        setSupervision(data)
        toast.success(t("app.notes.toast.weeklySummary"))
      } catch {
        toast.error(t("app.notes.toast.summaryUnavailable"))
      }
    })

  const aiRedraft = async () => {
    if (!selected) return
    ensureAiConsent(async () => {
      try {
        const { data } = await axios.post(`${API}/ai/notes/redraft`, {
          note_id: selected.id,
          ...draft,
          client_name: selected.client_name,
          vertical: effectiveVertical,
        })
        const next = {
          subjective: data.subjective,
          objective: data.objective,
          assessment: data.assessment,
          plan: data.plan,
        }
        setDraft(next)
        await saveNote(selected.id, next)
        toast.success(t("app.notes.toast.aiRedraft"))
      } catch {
        toast.error(t("app.notes.toast.aiUnavailable"))
      }
    })
  }

  const startVoice = () => {
    ensureAiConsent(() => {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!SR) {
        toast.error(t("app.notes.toast.speechUnsupported"))
        return
      }
      const rec = new SR()
      rec.lang = "ro-RO"
      rec.onresult = async (e) => {
        const transcript = Array.from(e.results)
          .map((r) => r[0].transcript)
          .join(" ")
        try {
          const { data } = await axios.post(`${API}/ai/notes/from-transcript`, {
            transcript,
            client_name: selected?.client_name || "",
            vertical: effectiveVertical,
          })
          const next = {
            subjective: data.subjective,
            objective: data.objective,
            assessment: data.assessment,
            plan: data.plan,
          }
          setDraft(next)
          if (selected?.id) await saveNote(selected.id, next)
          toast.success(t("app.notes.toast.transcribed"))
        } catch {
          toast.error(t("app.notes.toast.transcribeFailed"))
        }
        setListening(false)
      }
      rec.onend = () => setListening(false)
      rec.start()
      setListening(true)
    })
  }

  const onPreviewTemplateChange = (value) => {
    setSelectedTemplateId(value)
    const tpl = templates.find((x) => x.id === value || x.name === value) || null
    setPreviewTemplate(tpl)
    if (tpl?.name) setNewTplName(tpl.name)
  }

  const onWorkingVerticalChange = (vert) => {
    setWorkingVertical(vert)
    setFilterCategory("__all__")
  }

  const createFromCompose = async () => {
    const client = clients.find((c) => c.id === filterClient)
    if (!client) return toast.error(t("app.notes.toast.selectClient"))
    const tpl =
      previewTemplate ||
      templates.find((x) => x.id === selectedTemplateId || x.name === selectedTemplateId) ||
      templates[0]
    if (!tpl) return toast.error(t("app.notes.toast.templatesFailed"))
    try {
      const payload = {
        client_id: client.id,
        client_name: client.name,
        subjective: tpl.subjective || "",
        objective: tpl.objective || "",
        assessment: tpl.assessment || "",
        plan: tpl.plan || "",
      }
      if (bootstrapAppointmentId) payload.appointment_id = bootstrapAppointmentId
      const { data } = await axios.post(`${API}/notes`, payload)
      setBootstrapAppointmentId("")
      toast.success(t("app.notes.toast.noteCreated"))
      await load()
      selectNote(data)
    } catch {
      toast.error(t("app.notes.toast.createFailed"))
    }
  }

  const createNote = async () => {
    const client = clients.find((c) => c.id === newClientId)
    if (!client) return toast.error(t("app.notes.toast.selectClient"))
    const tpl =
      templates.find((x) => x.name === newTplName || x.id === newTplName) || previewTemplate || templates[0]
    try {
      const payload = {
        client_id: client.id,
        client_name: client.name,
        subjective: tpl?.subjective || "",
        objective: tpl?.objective || "",
        assessment: tpl?.assessment || "",
        plan: tpl?.plan || "",
      }
      if (bootstrapAppointmentId) payload.appointment_id = bootstrapAppointmentId
      const { data } = await axios.post(`${API}/notes`, payload)
      setNewOpen(false)
      setBootstrapAppointmentId("")
      toast.success(t("app.notes.toast.noteCreated"))
      await load()
      selectNote(data)
    } catch {
      toast.error(t("app.notes.toast.createFailed"))
    }
  }

  const changeNoteTemplate = async (value) => {
    if (!selected || isSigned) return
    const tpl = templates.find((x) => x.id === value || x.name === value)
    if (!tpl) return
    if (!window.confirm(t("app.notes.templateChangeConfirm"))) return
    const next = {
      subjective: tpl.subjective || "",
      objective: tpl.objective || "",
      assessment: tpl.assessment || "",
      plan: tpl.plan || "",
    }
    setDraft(next)
    setEditorTemplateId(tpl.id || tpl.name)
    await saveNote(selected.id, next)
    toast.success(t("app.notes.toast.templateChanged"))
  }

  const seedRoTemplates = async () => {
    try {
      await axios.post(`${API}/note-templates/seed`, {})
      await refreshTemplates(workingVertical, filterCategory, selectedTemplateId)
      toast.success(t("app.notes.toast.templatesLoaded"))
    } catch {
      toast.error(t("app.notes.toast.templatesFailed"))
    }
  }

  const isSigned = selected?.status === "signed"
  const statusLabel = (s) => t(`app.notes.status.${s}`, { defaultValue: s })

  return (
    <FeatureGate
      feature="notes_basic"
      title={t("app.notes.gateTitle")}
      description={t("app.notes.gateDescription")}
    >
      <AppLayout
        title={t("app.notes.pageTitle")}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={runSupervision}>
              <ClipboardList strokeWidth={1.5} className="h-4 w-4 mr-1" />
              {t("app.notes.supervision")}
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              data-testid="notes-new"
              onClick={() => setNewOpen(true)}
            >
              <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" />
              {t("app.notes.newNote")}
            </Button>
          </div>
        }
      >
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.notes.intro")}{" "}
          <Link to="/app/clients" className="underline underline-offset-2">
            {t("app.nav.clients")}
          </Link>
          {" · "}
          <Link to="/app/appointments" className="underline underline-offset-2">
            {t("app.nav.appointments")}
          </Link>
          {" · "}
          <Link to="/app/care-plans" className="underline underline-offset-2">
            {t("app.nav.carePlans")}
          </Link>
        </p>

        <PilotBanner className="mb-4 max-w-2xl">{t("app.notes.aiPilotHint")}</PilotBanner>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">{t("app.notes.scopeHint")}</p>

        {filterClient && filterClientName && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40">
              {t("app.notes.filteredBy", { name: filterClientName })}
              <button type="button" className="text-muted-foreground hover:text-foreground" onClick={clearClientFilter}>
                ×
              </button>
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            size="sm"
            variant={filterStatus === "" ? "default" : "outline"}
            className="rounded-full h-8"
            onClick={() => setFilterStatus("")}
          >
            {t("app.notes.filterAll")} ({summary.total || 0})
          </Button>
          {NOTE_STATUSES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filterStatus === s ? "default" : "outline"}
              className="rounded-full h-8 capitalize"
              onClick={() => setFilterStatus(s)}
            >
              {statusLabel(s)} ({summary[s] || 0})
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-6 max-w-6xl">
          <aside className="lg:col-span-2 space-y-3">
            <div className="rounded-xl border border-border bg-card p-3 space-y-3">
              <h3 className="text-sm font-medium">{t("app.notes.listTitle")}</h3>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="pl-8 h-8 text-sm"
                  placeholder={t("app.notes.searchPlaceholder")}
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">{t("app.notes.filterClient")}</Label>
                <Select
                  value={filterClient || "__all__"}
                  onValueChange={(v) => {
                    if (v === "__all__") clearClientFilter()
                    else setFilterClient(v)
                  }}
                >
                  <SelectTrigger className="mt-0.5 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t("app.notes.filterClientAll")}</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <ul
              className="rounded-xl border border-border divide-y bg-card text-sm max-h-[52vh] overflow-auto"
              data-testid="notes-list"
            >
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground text-xs">
                  {t("app.notes.loading")}
                </li>
              )}
              {!loading &&
                visibleNotes.map((n) => (
                  <li key={n.id} className="group">
                    <div
                      className={`flex items-start gap-2 px-3 py-2.5 ${
                        selected?.id === n.id ? "bg-primary/10" : "hover:bg-muted/50"
                      }`}
                    >
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left"
                        onClick={() => selectNote(n)}
                        data-testid={`note-item-${n.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <FileText strokeWidth={1.5} className="h-3.5 w-3.5 text-accent shrink-0" />
                          <p className="font-medium text-[13px] truncate">{n.client_name}</p>
                          {n.status === "signed" && (
                            <Lock className="h-3 w-3 text-muted-foreground ml-auto shrink-0" />
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 pl-5">
                          {n.subjective || "—"}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1.5 pl-5">
                          <Badge variant={statusBadgeVariant(n.status)} className="text-[10px] h-5 capitalize">
                            {statusLabel(n.status || "draft")}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {formatDateTime(n.updated_at || n.created_at)}
                          </span>
                        </div>
                      </button>
                      {n.status !== "signed" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("app.notes.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>{t("app.notes.deleteBody")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("app.notes.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteNote(n.id)}>
                                {t("app.notes.confirmDelete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </li>
                ))}
              {!loading && !visibleNotes.length && (
                <li className="px-4 py-8 text-center text-muted-foreground text-xs space-y-2">
                  {filterClient ? (
                    <>
                      <p>{t("app.notes.emptyFiltered")}</p>
                      <Button size="sm" className="rounded-full h-8" onClick={openComposeForFiltered}>
                        <Plus strokeWidth={1.5} className="h-3.5 w-3.5 mr-1" />
                        {t("app.notes.newNoteForClient")}
                      </Button>
                    </>
                  ) : (
                    <p>{t("app.notes.selectNote")}</p>
                  )}
                </li>
              )}
            </ul>
          </aside>

          <section
            className="lg:col-span-3 rounded-xl border border-border bg-card p-5 lg:p-6 min-w-0"
            data-testid="note-editor"
            key={selected?.id || "empty"}
          >
            {selected && draftLoading && (
              <div className="h-96 grid place-items-center text-muted-foreground text-sm">
                {t("app.notes.loadingNote")}
              </div>
            )}
            {selected && draft && !draftLoading ? (
              <>
                <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                      {selected.type} · {statusLabel(selected.status)}
                    </p>
                    <h2 className="font-display text-xl tracking-tight font-medium mt-1 truncate">
                      {selected.client_name}
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected.client_id && (
                      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" asChild>
                        <Link to={`/app/clients?open=${selected.client_id}`}>
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t("app.notes.openClient")}
                        </Link>
                      </Button>
                    )}
                    {selected.client_id && (
                      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" asChild>
                        <Link to={`/app/care-plans?client=${selected.client_id}`}>
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t("app.notes.openCarePlans")}
                        </Link>
                      </Button>
                    )}
                    {selected.client_id && (
                      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" asChild>
                        <Link to={`/app/appointments?client=${selected.client_id}`}>
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t("app.notes.openCalendar")}
                        </Link>
                      </Button>
                    )}
                    {selected.appointment_id && (
                      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" asChild>
                        <Link to={`/app/appointments?client=${selected.client_id || ""}`}>
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t("app.notes.openSession")}
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full h-8"
                      onClick={startVoice}
                      disabled={isSigned || listening}
                    >
                      <Mic strokeWidth={1.5} className="h-4 w-4 mr-1" />
                      {listening ? t("app.notes.listening") : t("app.notes.voiceSoap")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full h-8"
                      data-testid="note-ai-draft"
                      onClick={aiRedraft}
                      disabled={isSigned}
                    >
                      <Sparkles strokeWidth={1.5} className="h-4 w-4 mr-1" />
                      {t("app.notes.aiRedraft")}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mb-2">
                  {t("app.notes.activeSpecialty")}:{" "}
                  <span className="font-medium text-foreground">{labelForVertical(workingVertical)}</span>
                  <span className="block text-[10px] mt-0.5">{t("app.notes.templateScopeHint")}</span>
                </p>
                {templates.length > 0 && (
                  <div className="mb-4 max-w-md">
                    <Label className="text-xs">{t("app.notes.selectTemplate")}</Label>
                    <Select
                      value={editorTemplateId || templates[0]?.id || templates[0]?.name || ""}
                      onValueChange={changeNoteTemplate}
                      disabled={isSigned}
                    >
                      <SelectTrigger className="mt-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {templates.map((tpl) => (
                          <SelectItem key={tpl.id || tpl.name} value={tpl.id || tpl.name}>
                            {tpl.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {templates.length} {t("app.notes.templatesAvailable")}
                    </p>
                  </div>
                )}

                <p className="text-[10px] text-muted-foreground mb-4">{t("app.notes.soapReorderHint")}</p>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => {
                    const { active, over } = e
                    if (!over || active.id === over.id) return
                    setSectionOrder((ord) => {
                      const a = ord.indexOf(String(active.id))
                      const b = ord.indexOf(String(over.id))
                      if (a < 0 || b < 0) return ord
                      return arrayMove(ord, a, b)
                    })
                  }}
                >
                  <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
                    <div className="space-y-5">
                      {sectionOrder.map((key) => (
                        <SortableSoapShell
                          key={key}
                          id={key}
                          label={soapLabels[key]}
                          isSigned={isSigned}
                        >
                          {key === "assessment" && !isSigned && showPsychNoteTools && (
                            <div className="mb-2">
                              <Input
                                placeholder={t("app.notes.icdPlaceholder")}
                                value={icdQuery}
                                onChange={(e) => searchIcd(e.target.value)}
                                className="text-sm mb-1"
                              />
                              {icdResults.length > 0 && (
                                <ul className="text-xs border rounded-lg divide-y max-h-32 overflow-y-auto">
                                  {icdResults.map((c) => (
                                    <li key={c.code}>
                                      <button
                                        type="button"
                                        className="w-full text-left px-2 py-1.5 hover:bg-muted"
                                        onClick={() => appendIcd(c.code, c.label)}
                                      >
                                        <span className="font-mono">{c.code}</span> — {c.label}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                          {isSigned ? (
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">{draft[key]}</p>
                          ) : (
                            <Textarea
                              value={draft[key] || ""}
                              onChange={(e) => onFieldChange(key, e.target.value)}
                              className="min-h-[80px] text-sm"
                            />
                          )}
                        </SortableSoapShell>
                      ))}
                      {selected?.client_id && !isSigned && showPsychNoteTools && (
                        <div className="rounded-xl border border-dashed border-border p-4">
                          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                            {t("app.notes.phq9Title")}
                          </p>
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              min={0}
                              max={27}
                              placeholder={t("app.notes.phq9Placeholder")}
                              value={phqScore}
                              onChange={(e) => setPhqScore(e.target.value)}
                              className="w-24"
                            />
                            <Button variant="outline" size="sm" className="rounded-full" onClick={submitPhq}>
                              {t("app.notes.phq9Save")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>

                {supervision && (
                  <div className="mt-5 p-4 rounded-xl bg-secondary/50 text-sm">
                    <p className="font-semibold text-xs uppercase tracking-wider mb-2">
                      {t("app.notes.supervisionTitle")}
                    </p>
                    <p>{supervision.summary}</p>
                    {supervision.red_flags?.map((f) => (
                      <p key={f} className="text-destructive text-xs mt-1">
                        • {f}
                      </p>
                    ))}
                  </div>
                )}

                {versions.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                      <History className="h-3 w-3" />
                      {t("app.notes.versionHistory")}
                    </p>
                    <ul className="text-xs space-y-1 max-h-24 overflow-y-auto">
                      {versions.map((v) => (
                        <li key={v.id}>
                          {formatDateTime(v.created_at)} — {v.subjective?.slice(0, 40)}…
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-8 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-muted-foreground">
                    {savedAt
                      ? t("app.notes.savedAt", { time: savedAt.toLocaleTimeString("ro-RO") })
                      : t("app.notes.unsaved")}
                  </p>
                  {!isSigned && (
                    <Button className="rounded-full" data-testid="note-sign" onClick={() => setSignOpen(true)}>
                      {t("app.notes.signAndLock")}
                    </Button>
                  )}
                </div>
              </>
            ) : !selected ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{t("app.notes.composeHint")}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t("app.notes.composeVertical")}</Label>
                    {verticalOptions.length > 1 ? (
                      <Select value={workingVertical} onValueChange={onWorkingVerticalChange}>
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {verticalOptions.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-1 h-9 flex items-center text-sm font-medium">
                        {labelForVertical(workingVertical)}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">{t("app.notes.specialtySyncHint")}</p>
                  </div>
                  <div>
                    <Label className="text-xs">{t("app.notes.filterClient")}</Label>
                    <Select
                      value={filterClient || "__none__"}
                      onValueChange={(v) => setFilterClient(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1 h-9">
                        <SelectValue placeholder={t("app.notes.selectClient")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t("app.notes.selectClient")}</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {categoryOptions.length > 0 && (
                  <div>
                    <Label className="text-xs">{t("app.notes.filterCategory")}</Label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="mt-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">{t("app.notes.filterCategoryAll")}</SelectItem>
                        {categoryOptions.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {t(`app.notes.categories.${cat}`, { defaultValue: cat })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {templates.length > 0 && (
                  <div>
                    <Label className="text-xs">{t("app.notes.selectTemplate")}</Label>
                    <Select
                      value={selectedTemplateId || templates[0]?.id || templates[0]?.name || ""}
                      onValueChange={onPreviewTemplateChange}
                    >
                      <SelectTrigger className="mt-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {templates.map((tpl) => (
                          <SelectItem key={tpl.id || tpl.name} value={tpl.id || tpl.name}>
                            {tpl.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {templates.length} {t("app.notes.templatesAvailable")}
                    </p>
                  </div>
                )}
                {previewTemplate?.name && (
                  <p className="text-xs text-muted-foreground">
                    {t("app.notes.templatePreview")}: <span className="font-medium">{previewTemplate.name}</span>
                  </p>
                )}
                <Button
                  type="button"
                  className="rounded-full"
                  onClick={createFromCompose}
                  disabled={templatesLoading || !previewTemplate || !filterClient}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t("app.notes.createFromTemplate")}
                </Button>
                {templatesLoading && (
                  <p className="text-sm text-muted-foreground">{t("app.notes.loading")}</p>
                )}
                {!templatesLoading && !templates.length && workingVertical && (
                  <p className="text-sm text-muted-foreground">{t("app.notes.noTemplatesForVertical")}</p>
                )}
                {!templatesLoading && previewTemplate && (
                  <div className="space-y-3 pt-2 border-t border-border">
                    {SECTIONS.map((key) => (
                      <div key={key}>
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                          {soapLabels[key]}
                        </p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {previewTemplate[key] || "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>

        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("app.notes.newDialogTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t("app.notes.clientLabel")}</Label>
                <Select value={newClientId} onValueChange={setNewClientId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t("app.notes.selectClient")} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("app.notes.templateLabel")}</Label>
                <Select value={newTplName} onValueChange={setNewTplName}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {templates.map((tpl) => (
                      <SelectItem key={tpl.id || tpl.name} value={tpl.id || tpl.name}>
                        {tpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full rounded-full text-xs"
                type="button"
                onClick={seedRoTemplates}
              >
                {t("app.notes.seedTemplates")}
              </Button>
              <Button className="w-full rounded-full" onClick={createNote}>
                {t("app.notes.create")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={signOpen} onOpenChange={setSignOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("app.notes.signDialogTitle")}</DialogTitle>
            </DialogHeader>
            <SignaturePad onSave={setSignatureData} disabled={false} />
            {signatureData && (
              <p className="text-xs text-accent">{t("app.notes.signatureCaptured")}</p>
            )}
            <Button className="w-full rounded-full mt-2" disabled={!signatureData} onClick={signNote}>
              {t("app.notes.confirmSign")}
            </Button>
          </DialogContent>
        </Dialog>

        <AlertDialog open={aiConsentOpen} onOpenChange={setAiConsentOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("app.notes.aiConsentTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("app.notes.aiConsentBody")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("app.notes.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  localStorage.setItem("asistentpersonal-ai-consent", "1")
                  setAiConsentOpen(false)
                  pendingAi?.()
                  setPendingAi(null)
                }}
              >
                {t("app.notes.accept")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppLayout>
    </FeatureGate>
  )
}
